import { toast } from "react-toastify";
//@ts-ignore
import { AxiosInstance, getApiRoot } from "@careinfo/axios-service";
import { accessToken } from "../helpers/sessionKey";
import { useEffect } from "react";

let instance: any;

// Initialize Axios instance
const initInstance = async () => {
  if (!instance) {
    instance = await AxiosInstance();
  }
};

const useDownload = () => {
  const handleBeforeUnload = (event: any) => {
    event.preventDefault();
    event.returnValue =
      "Download in progress. Are you sure you want to leave the page?";
  };

  const download = async (
    downloadPath: string,
    filename: string,
    selectedItems = ""
  ) => {
    await initInstance(); // Ensure Axios instance is initialized
    const BASE_URL = await getApiRoot();

    // Start the toast with the initial message and progress
    const toastId = toast("Starting download...", {
      type: "info",
      autoClose: false, // Disable auto-close
      draggable: false,
      progress: 0,
      closeButton: false,
    });

    try {
      // Step 1: Start the report generation (Initial POST request with JSON response)
      const response = await instance({
        method: "POST",
        url: `${downloadPath}`,
        data: { ids: selectedItems?.split(",") },
        headers: {
          Authorization: `JWT ${accessToken()}`,
          "Content-Type": "application/json",
        },
      });

      if (response.status !== 200) {
        throw new Error("Failed to start the report generation");
      }

      window.addEventListener("beforeunload", handleBeforeUnload);

      toast.update(toastId, {
        render: "Downloading...",
        progress: 0.1,
      });

      let progressValue = 0.1;
      const runProgressBar = () => {
        progressValue += 0.01;
        if (progressValue >= 0.9) progressValue = 0.9;
        toast.update(toastId, {
          progress: progressValue,
          autoClose: false,
        });
      };
      const progressInterval = setInterval(runProgressBar, 100);

      // Extract task_id for polling
      const { task_id } = response.data;
      const pollAsyncUrl = `/download/api/download-history/check-for-download/?task_id=${task_id}`;

      // Step 2: Poll for the report generation status
      const pollResults = async (attempt = 1) => {
        try {
          const asyncResponse = await instance({
            method: "GET",
            url: pollAsyncUrl,
            headers: {
              Authorization: `JWT ${accessToken()}`,
            },
            responseType: "blob", // We expect a blob (file) as the response
            validateStatus: (status) => {
              // Handle both JSON (202) and Blob (200) responses
              return (status >= 200 && status < 300) || status === 202;
            },
          });

          if (asyncResponse.status === 202) {
            // Polling continues if status is 202
            setTimeout(() => pollResults(attempt + 1), 500);
          } else if (asyncResponse.status === 200) {
            // When status is 200, the file (blob) is ready for download
            clearInterval(progressInterval);
            window.removeEventListener("beforeunload", handleBeforeUnload);

            toast.update(toastId, {
              render: "Report generation completed...",
              progress: 0.66,
            });

            // Step 3: Create a download link and trigger download
            const a = document.createElement("a");
            const fileURL = window.URL.createObjectURL(asyncResponse.data);
            document.body.appendChild(a);
            a.style.display = "none";
            a.href = fileURL;
            a.download = filename;
            a.click();

            toast.update(toastId, {
              render: "Download complete!",
              type: "success",
              progress: 1,
            });
          }
        } catch (error) {
          clearInterval(progressInterval);
          window.removeEventListener("beforeunload", handleBeforeUnload);
          toast.update(toastId, {
            render: "An error occurred while downloading",
            type: "error",
            progress: 0,
            closeButton: true,
          });
        }
      };

      // Start polling for the file download
      pollResults();
    } catch (error) {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      toast.update(toastId, {
        render: "An error occurred while downloading.",
        type: "error",
        progress: 0,
        closeButton: true,
      });
    }
  };

  return { download };
};

export default useDownload;
